import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import clsx from 'clsx';
import { Component, createRef } from 'react';
import { GlobalHotKeys } from 'react-hotkeys';

import type { IconButtonTypeMap } from '@mui/material/IconButton';
import type { ElementType, ReactNode, RefObject } from 'react';
import type { KeySequence } from 'react-hotkeys';
import type { IToolbarEventsHandlers } from './use-toolbar-filter.js';

export interface IToolbarItem {
	id: string;
	title: string;
	defaultKeyBinding?: KeySequence;
	iconElement: ElementType;
	includeInHistory: boolean;
	isExclusive?: boolean;
	oneShot?: boolean;
	onKeyEvent?: (e: KeyboardEvent | undefined) => void;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	onActiveStateChanged?: (isActive: boolean) => void;
}

export type ToolbarItemProps = {
	active: boolean;
	showTooltipOpen?: boolean;
	onShouldActivate?: (item: IToolbarItem) => void;
	onShouldDeactivate?: (item: IToolbarItem) => void;
	buttonProps?: IconButtonTypeMap['props'];
	toolbarEvents: IToolbarEventsHandlers;
};

export interface IToolbarItemState {
	buttonProps: IconButtonTypeMap['props'];
	tooltipOpen: boolean;
	buttonRef: any;
}

export function GetToolbarItemClassName(item: string | IToolbarItem) {
	return `toolbar-item-${typeof item === 'string' ? item : item.id}`;
}

export abstract class ToolbarItem<S extends IToolbarItemState, P extends ToolbarItemProps = ToolbarItemProps>
	extends Component<P, S>
	implements IToolbarItem
{
	public static readonly ToolUsedDebounceMs = 10000;
	public static readonly ActivationDebounceTimeMs = 100;
	abstract id: string;
	abstract title: string;
	abstract defaultKeyBinding: KeySequence | undefined;
	abstract iconElement: ElementType;
	abstract keyEventName: string | undefined;
	abstract includeInHistory: boolean;
	protected defaultState: IToolbarItemState = {
		buttonProps: {},
		tooltipOpen: false,
		buttonRef: null,
	};

	public abstract onClick(e: React.MouseEvent<HTMLButtonElement>): void;
	public abstract onKeyEvent(e: KeyboardEvent | undefined): void;
	public abstract onActiveStateChanged(isActive: boolean): void;
	protected renderActive?: (isActive: boolean) => ReactNode;
	protected ref: RefObject<HTMLButtonElement | null> = createRef();
	private lastActivationChange: number | undefined;

	public override componentDidUpdate(prevProps: ToolbarItemProps) {
		if (prevProps.active !== this.props.active) {
			this.onActiveStateChanged(this.props.active);
		}
	}

	public override componentWillUnmount() {
		if (this.props.active) {
			this.onActiveStateChanged(false);
		}
	}

	public override render() {
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		const state = this.state ?? this.defaultState;
		const active = this.props.active && !this.isOneShot();
		return (
			<>
				{this.keyEventName !== undefined && this.defaultKeyBinding !== undefined && (
					<GlobalHotKeys
						keyMap={{
							[this.keyEventName]: this.defaultKeyBinding,
						}}
						handlers={{
							[this.keyEventName]: (e) => this.onKey(e),
						}}
					/>
				)}
				<Tooltip
					arrow
					title={
						<p className='text-sm'>
							{this.title} {this.renderKeys(this.defaultKeyBinding)}
						</p>
					}
					placement='right'
					open={Boolean(this.props.showTooltipOpen ?? state.tooltipOpen)}
					onOpen={() => this.setState({ ...state, tooltipOpen: true })}
					onClose={() => this.setState({ ...state, tooltipOpen: false })}
				>
					<IconButton
						disabled={this.isDisabled()}
						{...this.props.buttonProps}
						{...state.buttonProps}
						className={clsx(GetToolbarItemClassName(this), '-m-1 p-1', active && 'bg-gray-a4')}
						onClick={(e) => this.onClicked(e)}
						ref={this.ref}
					>
						<this.iconElement />
					</IconButton>
				</Tooltip>
				{this.renderActive && this.renderActive(active)}
			</>
		);
	}

	protected renderKeys(keys: KeySequence | undefined): React.ReactNode {
		if (typeof keys === 'string' || Array.isArray(keys)) {
			if (typeof keys === 'string') {
				keys = keys.split('+');
			}

			return (
				<>
					{(keys as string[]).map((key) => (
						<span key={key} className='key-button'>
							<kbd>{key.toLowerCase()}</kbd>
						</span>
					))}
				</>
			);
		}

		return undefined;
	}

	public renderMyKeys() {
		return this.renderKeys(this.defaultKeyBinding);
	}

	protected onKey(e: KeyboardEvent | undefined): void {
		this.onKeyEvent(e);
		if (e) {
			this.maybeActivate();
		}
	}

	protected maybeActivate(ignoreOneshot = false) {
		if (
			this.isDisabled() ||
			(this.isOneShot() && !ignoreOneshot) ||
			(this.lastActivationChange !== undefined &&
				Date.now() - this.lastActivationChange < ToolbarItem.ActivationDebounceTimeMs)
		) {
			return;
		}

		if (this.props.active && this.props.onShouldDeactivate) {
			this.lastActivationChange = Date.now();
			this.props.onShouldDeactivate(this);
		} else if (!this.props.active && this.props.onShouldActivate) {
			this.lastActivationChange = Date.now();
			this.props.onShouldActivate(this);
		}
	}

	protected maybeDeactivate() {
		if (
			this.lastActivationChange !== undefined &&
			Date.now() - this.lastActivationChange < ToolbarItem.ActivationDebounceTimeMs
		) {
			return;
		}
		if (this.props.active && this.props.onShouldDeactivate) {
			this.lastActivationChange = Date.now();
			this.props.onShouldDeactivate(this);
		}
	}

	protected onClicked(e: React.MouseEvent<HTMLButtonElement>): void {
		this.onClick(e);
		this.maybeActivate();
		e.stopPropagation();
	}

	protected isOneShot(): boolean {
		return Boolean((this as IToolbarItem).oneShot);
	}

	protected isDisabled(): boolean {
		return false;
	}

	protected emitToolUsed(args?: object): void {
		this.props.toolbarEvents.toolUsed(this.id, this, args);
	}
}
